import { IAttendanceDay, IAttendanceInfo, IAttendanceItem } from '../interfaces/attendance.interface'

export type AttendanceCardType = 'onSite' | 'overTime' | 'alerts' | 'away' | 'underperforming'

// An alert is a day with an invalid clocking. The Alerts card lists employees with one; the location header counts the days.
export const isAlertDay = (day: IAttendanceDay) => day.isValid === false

// The one definition of what each KPI card means — the card filter and the per-location counts both read it.
export const AttendanceCardRules: Record<AttendanceCardType, (employee: IAttendanceItem) => boolean> = {
  onSite: (el) => el.onSite,
  overTime: (el) => el.overTimeMinutes > 0 && el.includedInOvertime,
  alerts: (el) => !!el.days?.some(isAlertDay),
  away: (el) => !!el.days?.some((d) => d.workedAway),
  underperforming: (el) => el.isUnderperforming
}

// Cards restored from the URL are plain strings — anything that is not a known card is dropped. Own keys only:
// `in` would also accept inherited ones such as 'constructor'.
export const toAttendanceCards = (values: string[]) =>
  values.filter((value): value is AttendanceCardType => Object.hasOwn(AttendanceCardRules, value))

// Selected cards accumulate with AND: an employee is listed only when every selected card applies to them.
export const employeeMatchesCards = (employee: IAttendanceItem, cards: AttendanceCardType[]) =>
  cards.every((card) => AttendanceCardRules[card](employee))

// The one place the cards decide what is listed: each location keeps only the employees every selected card applies
// to, and a location left with none is dropped. No card selected leaves the list untouched.
export const filterLocationsByCards = (locations: IAttendanceInfo[], cards: AttendanceCardType[]) =>
  cards.length < 1
    ? locations
    : locations
        .map((location) => ({
          ...location,
          employees: (location.employees ?? []).filter((employee) =>
            employeeMatchesCards(employee, cards)
          )
        }))
        .filter((location) => location.employees.length > 0)
